// Step-by-step guides per DNS host, keyed by the provider the server detected from
// the domain's nameservers (server/modules/emailDomains/domainInspect.js).
// Button names change from time to time, so the steps describe where to go, not pixel paths.

/** relative = the host wants only the part before the zone (it appends the zone itself) */
export const DNS_PROVIDERS = {
  cloudflare: {
    name: 'Cloudflare',
    url: 'https://dash.cloudflare.com',
    relative: false,
    steps: [
      'נכנסים ל-dash.cloudflare.com ובוחרים את הדומיין.',
      'בתפריט הצד: DNS ← Records, ואז לוחצים Add record.',
      'בוחרים את ה-Type, מדביקים את ה-Name ואת ה-Content/Target מהטבלה.',
      'ברשומות CNAME: מכבים את הענן הכתום (Proxy status = DNS only, ענן אפור). זה הכרחי. עם ענן כתום האימות לא יעבור.',
      'TTL נשאר Auto. לוחצים Save, וחוזרים על הפעולה לכל רשומה.',
    ],
    zoneImport: 'אפשר גם לייבא את כל הרשומות בבת אחת: DNS ← Records ← Import and Export ← Import, ולבחור את קובץ הרשומות שהורדתם כאן. השאירו את האפשרות Proxy imported DNS records כבויה.',
  },
  godaddy: {
    name: 'GoDaddy',
    url: 'https://dcc.godaddy.com/control/portfolio',
    relative: true,
    steps: [
      'נכנסים לחשבון GoDaddy ← My Products ← Domains, ובוחרים את הדומיין.',
      'נכנסים ל-DNS (Manage DNS) ולוחצים Add New Record.',
      'בוחרים Type. בשדה Name מדביקים את השם המקוצר מהטבלה. GoDaddy מוסיף את הדומיין לבד.',
      'בשדה Value מדביקים את הערך, משאירים TTL ברירת מחדל ושומרים.',
    ],
  },
  livedns: {
    name: 'LiveDNS',
    url: 'https://www.livedns.co.il',
    relative: true,
    steps: [
      'נכנסים לאזור האישי ב-LiveDNS ובוחרים את הדומיין.',
      'נכנסים לניהול רשומות ה-DNS (ניהול DNS / עריכת רשומות).',
      'מוסיפים רשומה חדשה מהסוג שבטבלה. בשדה השם מדביקים את השם המקוצר, ובשדה הערך את הערך.',
      'שומרים, וחוזרים על הפעולה לכל רשומה.',
      'אם הדומיין מוגדר אצלכם עם שרתי DNS של חברת אחסון אחרת, את הרשומות מוסיפים שם ולא ב-LiveDNS.',
    ],
  },
  route53: {
    name: 'Amazon Route 53',
    url: 'https://console.aws.amazon.com/route53/v2/hostedzones',
    relative: true,
    steps: [
      'נכנסים ל-Route 53 ← Hosted zones ובוחרים את הדומיין.',
      'לוחצים Create record. בשדה Record name מדביקים את השם המקוצר.',
      'בוחרים Record type ומדביקים את הערך. ב-TXT הערך צריך להיות בתוך מירכאות כפולות.',
      'לוחצים Create records.',
    ],
    zoneImport: 'אפשר גם לייבא: Hosted zone ← Import zone file, ולהדביק את תוכן קובץ הרשומות.',
  },
  wix: {
    name: 'Wix',
    url: 'https://manage.wix.com/account/domains',
    relative: true,
    steps: [
      'נכנסים ל-Wix ← Domains. ליד הדומיין לוחצים על ⋯ ← Manage DNS Records.',
      'בקטע של סוג הרשומה (CNAME או TXT) לוחצים + Add Record.',
      'בשדה Host Name מדביקים את השם המקוצר, ובשדה Value את הערך.',
      'לוחצים Save DNS.',
    ],
  },
  namecheap: {
    name: 'Namecheap',
    url: 'https://ap.www.namecheap.com/domains/list',
    relative: true,
    steps: [
      'נכנסים ל-Domain List ← Manage ליד הדומיין ← Advanced DNS.',
      'לוחצים Add New Record ובוחרים את הסוג.',
      'בשדה Host מדביקים את השם המקוצר, ובשדה Value / Target את הערך.',
      'לוחצים על ✓ לשמירה.',
    ],
  },
  google: {
    name: 'Google Domains / Squarespace',
    url: 'https://account.squarespace.com/domains',
    relative: true,
    steps: [
      'דומיינים של Google Domains עברו ל-Squarespace. נכנסים ל-Squarespace ← Domains ובוחרים את הדומיין.',
      'נכנסים ל-DNS ← DNS Settings ← Custom records ← Add record.',
      'בשדה Host מדביקים את השם המקוצר, בוחרים Type ומדביקים את הערך.',
    ],
  },
  squarespace: null, // same guide as google
  hostinger: {
    name: 'Hostinger',
    url: 'https://hpanel.hostinger.com/domains',
    relative: true,
    steps: [
      'נכנסים ל-hPanel ← Domains ← הדומיין ← DNS / Nameservers.',
      'בקטע Manage DNS records בוחרים Type, ובשדה Name מדביקים את השם המקוצר.',
      'מדביקים את הערך בשדה Points to / TXT value ולוחצים Add Record.',
    ],
  },
  azure: {
    name: 'Azure DNS',
    url: 'https://portal.azure.com/#browse/Microsoft.Network%2FdnsZones',
    relative: true,
    steps: [
      'נכנסים ל-DNS zones ובוחרים את הדומיין.',
      'לוחצים + Record set, מדביקים את השם המקוצר ב-Name, בוחרים Type ומדביקים את הערך.',
    ],
  },
  ovh: {
    name: 'OVHcloud',
    url: 'https://www.ovh.com/manager/',
    relative: true,
    steps: [
      'נכנסים ל-Web Cloud ← Domain names ← הדומיין ← DNS zone.',
      'לוחצים Add an entry, בוחרים את הסוג, מדביקים את השם המקוצר בשדה Sub-domain ואת הערך בשדה Target.',
    ],
  },
  digitalocean: {
    name: 'DigitalOcean',
    url: 'https://cloud.digitalocean.com/networking/domains',
    relative: true,
    steps: [
      'נכנסים ל-Networking ← Domains ובוחרים את הדומיין.',
      'בוחרים את סוג הרשומה. בשדה Hostname מדביקים את השם המקוצר, ובשדה הערך את הערך. לוחצים Create Record.',
    ],
  },
};
DNS_PROVIDERS.squarespace = DNS_PROVIDERS.google;

export const GENERIC_PROVIDER = {
  name: 'ספק ה-DNS שלכם',
  relative: true,
  steps: [
    'נכנסים לאתר שבו מנהלים את הדומיין: רשם הדומיין או חברת האחסון של האתר.',
    'מחפשים את ניהול ה-DNS (DNS Zone / ניהול רשומות / Advanced DNS).',
    'מוסיפים כל רשומה מהטבלה בנפרד: בוחרים את הסוג, מדביקים את השם ואת הערך, ושומרים.',
    'ברוב הספקים מזינים בשדה השם רק את השם המקוצר, כי הספק מוסיף את הדומיין לבד. אם השדה מבקש שם מלא, השתמשו בשם המלא.',
  ],
};

export const providerGuide = (key) => (key && DNS_PROVIDERS[key]) || GENERIC_PROVIDER;

export const PROVIDER_OPTIONS = Object.entries(DNS_PROVIDERS)
  .filter(([key]) => key !== 'squarespace')
  .map(([key, p]) => ({ key, name: p.name }));

export const MAIL_PROVIDER_NAMES = { google: 'Google Workspace', microsoft: 'Microsoft 365', other: 'ספק דואר קיים' };

/** the name as typed at the DNS host: relative to the zone (the host appends it), "@" for the zone itself */
export function relativeName(name, zone) {
  if (!zone) return name;
  if (name === zone) return '@';
  return name.endsWith(`.${zone}`) ? name.slice(0, -(zone.length + 1)) : name;
}

/** BIND snippet for hosts that import a zone file (Cloudflare, Route 53) */
export function zoneFile(records) {
  return `${records.map((r) => {
    const value = r.type === 'TXT' ? `"${r.value}"` : `${r.value}.`;
    return `${r.name}.\t3600\tIN\t${r.type}\t${value}`;
  }).join('\n')}\n`;
}
